// src/pages/PPP.js
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { Dialog } from "@headlessui/react";
import { MonitorIcon, XIcon, SearchIcon, MoreVerticalIcon } from "lucide-react";

const PPP = () => {
  const [profiles, setProfiles] = useState([]);
  const [devices, setDevices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode] = useState(false);
  const [selectedProfile] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [formData, setFormData] = useState({
    device_id: "",
    username: "",
    password: "",
    user_description: "",
  });
  const [searchKey, setSearchKey] = useState("username");
  const [searchValue, setSearchValue] = useState("");
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/userman`, {
        headers: { authorization: token },
      });
      if (response.data.status === 0) {
        setProfiles(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/devices`, {
        headers: { authorization: token },
      });
      if (response.data.status === 0) {
        setDevices(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfiles();
    fetchDevices();
  }, [fetchProfiles, fetchDevices]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    try {
      if (isEditMode && selectedProfile) {
        await api.put(`/userman/${selectedProfile.id}`, formData, {
          headers: { authorization: token },
        });
        alert("Profile Manager berhasil diperbarui!");
      } else {
        await api.post(`/userman`, formData, {
          headers: { authorization: token },
        });
        alert("Profile Manager berhasil ditambahkan!");
      }
      fetchProfiles();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save profile manager:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const response = await api.get(
        `/userman/search?${searchKey}=${searchValue}`,
        { headers: { authorization: token } }
      );
      if (response.data.status === 0) {
        setProfiles(response.data.data);
      } else {
        alert("Profile Manager tidak ditemukan");
      }
    } catch (error) {
      console.error("Failed to search profile managers:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MonitorIcon /> Mikrotik PPP
        </h1>

        <Link
          to="/ppp-secrets-all"
          className="px-3 py-2 rounded-md text-sm font-medium shadow-sm
             bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          All PPP Secrets
        </Link>
      </div>

      {/* Search */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className="flex gap-2 mb-6"
      >
        <select
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          className="border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        >
          {[
            "id",
            "device_id",
            "username",
            "user_description",
            "created_at",
            "updated_at",
          ].map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Cari..."
          className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 flex-1"
        />
        <button
          type="submit"
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <SearchIcon size={16} /> Search
        </button>
      </form>

      {/* Profile list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profiles.map((profile) => (
          <div
            key={profile.id}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 relative flex flex-col justify-between min-h-[180px]"
          >
            <div>
              <h2 className="text-lg font-bold mb-1 flex justify-between items-center">
                {profile.device_name}
                {profile.status === "Connected" && (
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenDropdownId(
                          openDropdownId === profile.id ? null : profile.id
                        )
                      }
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <MoreVerticalIcon size={18} />
                    </button>
                    {openDropdownId === profile.id && (
                      <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-900 shadow-lg rounded-md z-10 border border-gray-200 dark:border-gray-700">
                        <a
                          href={`/ppp-secrets?userman_id=${profile.id}&device_name=${profile.device_name}`}
                          className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Secrets
                        </a>
                        <a
                          href={`/ppp-profiles?userman_id=${profile.id}&device_name=${profile.device_name}`}
                          className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Profiles
                        </a>
                        <a
                          href={`/ppp-active-cons?userman_id=${profile.id}&device_name=${profile.device_name}`}
                          className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Active Connections
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Device ID : <b>{profile.device_id}</b>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Device Userman : <b>{profile.username}</b>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {profile.user_description}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-medium">Status:</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    profile.status === "Connected"
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {profile.status}
                </span>
              </div>
            </div>

            <div className="flex justify-end items-end mt-4">
              <p className="text-xs text-gray-400">
                {new Date(profile.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Panel className="bg-white dark:bg-gray-600 p-6 rounded-lg w-full max-w-md relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <XIcon size={20} />
            </button>
            <Dialog.Title className="text-lg font-bold text-black dark:text-white mb-4">
              {isEditMode ? "Edit Mikrotik User" : "Add Mikrotik User"}
            </Dialog.Title>
            <form onSubmit={handleSubmit} className="space-y-3">
              <select
                name="device_id"
                value={formData.device_id}
                onChange={handleChange}
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                required
              >
                <option value="">-- Pilih Device --</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.nama}
                  </option>
                ))}
              </select>
              <input
                name="username"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                required
              />
              <input
                name="password"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                required={!isEditMode}
              />
              <textarea
                name="user_description"
                placeholder="Deskripsi"
                value={formData.user_description}
                onChange={handleChange}
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                required
              ></textarea>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {isEditMode ? "Update Profile" : "Add Profile"}
              </button>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default PPP;
